import type { ElementType, ReactNode } from "react";
import type { Icon } from "@phosphor-icons/react";
import { cn } from "./cn";

/** Icon-box colours, the set the dashboard stat cards use. */
export const STAT_TONES = {
  violet: { bg: "bg-violet-500/10", fg: "text-violet-500" },
  blue: { bg: "bg-blue-500/10", fg: "text-blue-500" },
  emerald: { bg: "bg-emerald-500/10", fg: "text-emerald-500" },
  amber: { bg: "bg-amber-500/10", fg: "text-amber-500" },
  rose: { bg: "bg-rose-500/10", fg: "text-rose-500" },
  cyan: { bg: "bg-cyan-500/10", fg: "text-cyan-500" },
  neutral: { bg: "bg-muted", fg: "text-muted-foreground" },
} as const;

export type StatTone = keyof typeof STAT_TONES;

interface StatTileProps {
  label: string;
  value: ReactNode;
  icon?: Icon;
  tone?: StatTone;
  /** A line under the value: a period, a delta, a caveat. */
  hint?: ReactNode;
  /** Makes the whole tile a button, with the dashboard hover lift. */
  onClick?: () => void;
  /** Renders the tile as this element instead, e.g. a router Link, with these props. */
  component?: ElementType;
  componentProps?: Record<string, unknown>;
  className?: string;
}

/**
 * A number with a label and an icon in a tinted box: the tile the dashboard
 * stat grid is built from. A button when it goes somewhere, a plain tile when
 * it does not.
 */
export function StatTile({
  label,
  value,
  icon: TileIcon,
  tone = "violet",
  hint,
  onClick,
  component: Component,
  componentProps,
  className,
}: StatTileProps) {
  const colours = STAT_TONES[tone];
  const body = (
    <>
      {TileIcon && (
        <span
          className={cn(
            "inline-flex h-10 w-10 items-center justify-center rounded-xl",
            colours.bg
          )}
        >
          <TileIcon className={cn("h-5 w-5", colours.fg)} />
        </span>
      )}
      <p className={cn("text-xs text-muted-foreground", TileIcon && "mt-3")}>
        {label}
      </p>
      <p className="mt-0.5 text-2xl font-bold tabular-nums tracking-tight">
        {value}
      </p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </>
  );
  const base =
    "rounded-xl border border-border/50 bg-card p-4 text-left sm:p-5";

  const lift =
    "transition-all hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  if (Component) {
    return (
      <Component
        {...componentProps}
        className={cn(base, "block", lift, className)}
      >
        {body}
      </Component>
    );
  }

  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={cn(base, lift, className)}
    >
      {body}
    </button>
  ) : (
    <div className={cn(base, className)}>{body}</div>
  );
}
